import MacheteConstants from '@hover/machete-constants';

import createDebug from 'debug';
import {
  all,
  call,
  fork,
  put,
  race,
  select,
  take,
  takeLatest,
} from 'redux-saga/effects';
import { get as lodashGet, throttle } from 'lodash';
import {
  actions as macheteActions,
  selectors as macheteReduxSelectors,
  getModel,
  getViewer,
  utils as macheteReduxUtils,
} from 'src/machete-redux';
import theme from 'src/theme';
import EventNames from 'src/lib/eventNames';
import constants from 'src/constants';
import utils from 'src/utils';
import trackingDux from 'src/redux/tracking';
import { shouldFetchInitialModelTexturesSeparately } from 'src/featureFlags';
import GraphQLApi from 'src/apis/GraphQLApi';
import macheteDux from './index';
import jobDesignsDux from '../jobDesigns';
import appDux from '../app';
import undoDux from '../undo';
import uiDux from '../ui';
import macheteEventChannels from './macheteEventChannels';

const debug = createDebug('designPro.machete.sagas');

const { ORIGINAL_DESIGN_ID } = constants.jobDesign;
const {
  SECOND_ORDER_DETAIL_ATTRIBUTES_BY_PARAMETRIC_TYPE,
  SECOND_ORDER_DETAIL_PARAMETRIC_TYPES,
  PARAMETRIC_TYPES,
} = constants.machete;

const ZOOM_STEP = 1.0; // 1.0 meters
const { isGlass, getNodeUuidFromGlassNode, isValidTexture } = utils.machete;

function zoomMacheteView(step) {
  const { controls } = getViewer();

  controls.dollyByParams({
    step, // distance in meters
    direction: controls.getPivotZoomDirection(),
  });

  controls.needsUpdate = true;
}

export function panCameraSaga(action) {
  const { viewName } = action.payload;
  const VIEW_MAP = {
    left: MacheteConstants.LeftMoneyshot,
    right: MacheteConstants.RightMoneyshot,
    front: MacheteConstants.FrontMoneyshot,
    back: MacheteConstants.BackMoneyshot,
  };
  const viewOptions = {
    useOrbit: true,
    duration: 1000,
  };
  const model = getModel();
  const viewer = getViewer();
  /**
   * setting initial view to set some default values for focus
   * Note this is a temporary fix until we can get the fix in machete
   * JIRA: NS-671
   * TODO: Remove after the fix in machete
   */
  viewer.focus(model, viewOptions);
  if (viewName !== 'main') viewOptions.side = VIEW_MAP[viewName];
  viewer.focus(model, viewOptions);
}

export function zoomInSaga() {
  zoomMacheteView(-ZOOM_STEP);
}

export function zoomOutSaga() {
  zoomMacheteView(ZOOM_STEP);
}

export function* updateSelection({
  payload: {
    hit,
    event: { shiftKey },
  },
}) {
  const shouldAllowUpdateSelection = yield select(
    macheteDux.selectors.shouldAllowUpdateSelection,
  );
  const isMultiSelectToolActive = yield select(
    appDux.selectors.isSelectToolActive,
  );

  const mode = yield select(appDux.selectors.getActiveMode);
  const isPreviewMode = mode === null;

  // open design mode when clicking model in preview mode
  if (isPreviewMode && hit && hit.uuid) {
    const secondRapidClickEventChannel = yield call(
      macheteEventChannels.secondRapidClick,
    );
    const hasCaughtSecondRapidClick = yield take(secondRapidClickEventChannel);

    if (!hasCaughtSecondRapidClick) {
      yield put(uiDux.actions.handleDesignModeClick());
      return;
    }
  }

  // return early unless clicking to select elements is allowed
  if (!shouldAllowUpdateSelection) return;

  const visibleNodeIds = yield select(macheteDux.selectors.getVisibleNodeIdSet);
  const selectedNodeIds = yield select(
    macheteReduxSelectors.segmentation.getSelectedNodeIds,
  );

  if (hit) {
    let { uuid } = hit;
    if (!visibleNodeIds.has(uuid)) return;

    const node = macheteReduxUtils.getNode(uuid);
    if (isGlass(node)) uuid = getNodeUuidFromGlassNode(node);

    if (isMultiSelectToolActive || shiftKey) {
      if (selectedNodeIds.includes(uuid)) {
        yield put(macheteActions.segmentation.removeNodeSelection(uuid));
      } else {
        yield put(macheteActions.segmentation.addNodeSelection(uuid));
      }
    } else {
      yield put(macheteActions.segmentation.setNodeSelection([uuid]));
    }

    yield put(appDux.actions.setBreadcrumbData({}));
  }
}

function* handlePointerEventsSaga() {
  // max distance in px between "down" and "up" events that still triggers a click
  const DISTANCE_THRESHOLD = 5;
  const startPoint = new Machete.Vector2();
  const endPoint = new Machete.Vector2();

  while (true) {
    const action = yield take(macheteActions.segmentation.pointerDown);
    const isScreenshotModeActive = yield select(
      appDux.selectors.getIsScreenshotModeActive,
    );

    const {
      payload: {
        event: { clientX: x1, clientY: y1 },
      },
    } = action;
    startPoint.set(x1, y1);
    let mouseIsDown = true;
    let distance = 0;

    while (mouseIsDown) {
      const { up, move } = yield race({
        up: take(macheteActions.segmentation.pointerUp),
        move: take(macheteActions.segmentation.pointerMove),
      });

      if (up) mouseIsDown = false;
      if (move) {
        const {
          payload: {
            event: { clientX: x2, clientY: y2 },
          },
        } = move;
        endPoint.set(x2, y2);
        distance = startPoint.distanceTo(endPoint);
      }
    }

    if (!isScreenshotModeActive && distance < DISTANCE_THRESHOLD) {
      yield call(updateSelection, action);
    }
  }
}

export function* updateModelElementOutlines() {
  const visibleNodes = yield select(macheteDux.selectors.getVisibleNodes);
  const selectedNodeIds = yield select(
    macheteReduxSelectors.segmentation.getSelectedNodeIds,
  );

  debug(`selected node ids (${selectedNodeIds.length}): `, selectedNodeIds);
  visibleNodes.forEach(({ uuid }) => {
    const node = macheteReduxUtils.getNode(uuid);
    const { currentModelElements = [] } = node;
    currentModelElements.forEach((el) => {
      if (selectedNodeIds.includes(uuid)) {
        el.includeOutline = true;
        el.outline.configure({
          color: theme.colors.red,
        });
      } else {
        el.includeOutline = false;
      }
    });
  });
}

export function updateFeatureTextureSaga({ payload: { nodeIds, update } }) {
  const { color, texture, materialId } = update;

  const model = getModel();

  nodeIds.forEach((uuid) => {
    const feature = model.featureAPI.getFeatureByUUID(uuid);

    if (isValidTexture(texture)) {
      feature.setAttribute({ texture: texture.textureData.id });
    }

    feature.setAttribute({ material_id: materialId });

    feature.setAttribute({ color });
  });
}

export function updateElementTextureSaga({ payload: { nodeIds, update } }) {
  const { color, texture, materialId } = update;

  const model = getModel();
  const tree = model.segmentationTree;
  const autoUpdateValue = tree.autoUpdate;
  tree.autoUpdate = false;

  nodeIds.forEach((uuid) => {
    const node = macheteReduxUtils.getNode(uuid);

    // if the texture is present, update the 'texture_id' on the node
    // example value '1340' (texture id is a Number)
    if (isValidTexture(texture)) {
      tree.performOperation(node, 'texture_id', texture.textureData.id);
    }

    // update the color_hex value,
    // example value: '#ff0000' (red)
    tree.performOperation(node, 'color_hex', color);
    tree.performOperation(node, 'primary_label_id', materialId);
  });

  tree.update();
  tree.autoUpdate = autoUpdateValue;
}

export function* handleNodeSelectionChangedSaga() {
  // update which model elements are outlined
  yield call(updateModelElementOutlines);
  // close extended sidebar and set activeEditingStyle to null
  yield put(uiDux.actions.closeExtendedSidebar());
  yield put(appDux.actions.setActiveEditingStyle({}, {}));
}

export function* loadModelSaga({ modelData }, shouldFocus) {
  const hasFinsihedFetchingProducts = yield select(
    appDux.selectors.isProductCatalogInitialized,
  );

  if (!hasFinsihedFetchingProducts) {
    yield take(appDux.actions.setProductCatalogInitialized);
  }

  const products = yield select(macheteDux.selectors.getProducts);
  yield put(
    undoDux.actions.setUndoRedoStacks({ undoStack: [], redoStack: [] }),
  );

  modelData.opts.parametricOptionsByType =
    Hover.Constants.BUILDING_DIM_3_PARAMETRIC_OPTIONS_BY_TYPE;

  modelData.opts.useSyntheticGlassAnnotations = true;

  modelData.opts.products = products;

  yield put(macheteActions.model.load({ options: modelData.opts }));
  yield take(macheteActions.model.ready);

  const model = getModel();
  const jobDesigns = yield select(jobDesignsDux.selectors.getJobDesigns);
  const newJobDesigns = { ...jobDesigns };
  const selectedDesignId = yield select(
    jobDesignsDux.selectors.getSelectedDesignId,
  );

  if (selectedDesignId === ORIGINAL_DESIGN_ID) {
    newJobDesigns[ORIGINAL_DESIGN_ID].model = model;
  } else {
    newJobDesigns.saved[0].model = model;
  }

  yield put(jobDesignsDux.actions.receiveJobDesigns(newJobDesigns));

  if (shouldFocus) {
    yield put(macheteActions.model.focus());
  }

  yield put(macheteActions.segmentation.init());

  yield take(macheteActions.segmentation.ready);
  yield put(macheteActions.segmentation.enablePointer());
  yield put(
    macheteActions.segmentation.updatePointerConfig({
      enabledEvents: {
        pointerUp: true,
        pointerDown: true,
        pointerMove: true,
      },
      uuidOnly: false,
    }),
  );

  yield fork(trackWebGLCapabilitiesSaga);
}

export function* setTexturesV2() {
  yield all([
    take(macheteActions.segmentation.ready),
    take(appDux.actions.setProductCatalogInitialized),
  ]);

  const products = yield select(macheteDux.selectors.getProducts);
  const tree = lodashGet(
    Hover,
    'Machete.viewer.modelContainer.models[0].segmentationTree',
    {},
  );

  const autoUpdateValue = tree.autoUpdate;
  tree.autoUpdate = false;
  window.Hover.TexturingLibraryV2.setTextures(products);
  tree.autoUpdate = autoUpdateValue;
}

export function* textureModelSaga() {
  const sagaForModelTexturing = shouldFetchInitialModelTexturesSeparately()
    ? textureModelSaga_shouldFetchInitialModelTexturesSeparately
    : textureModelSaga_orig;

  yield call(sagaForModelTexturing);
}

export function* textureModelSaga_orig() {
  yield all([
    take(macheteActions.model.ready),
    take(appDux.actions.setProductCatalogInitialized),
  ]);
  /*
    We still texture models manually here despite machete
    now auto texturing because its faster! Do not remove!!
  */
  const products = yield select(macheteDux.selectors.getProducts);

  getModel().elements.forEach((modelElement) => {
    const { colorHex, textureId } = modelElement.manowarData;

    if (products[textureId]) {
      modelElement.textureFromData(products[textureId]);
    }

    modelElement.color = colorHex;
  });
}

export function* textureModelSaga_shouldFetchInitialModelTexturesSeparately() {
  yield take(macheteActions.model.ready);
  const { elements } = getModel();
  const legacyTextureIds = elements.reduce((acc, modelElement) => {
    const { textureId } = modelElement.manowarData;
    if (textureId) {
      acc.add(textureId);
    }
    return acc;
  }, new Set());

  const { data: textures } = yield call(
    [GraphQLApi, GraphQLApi.getTextures],
    Array.from(legacyTextureIds),
  );

  elements.forEach((modelElement) => {
    const { colorHex, textureId } = modelElement.manowarData;
    const texture = textures.find((texture) => texture.id === textureId);

    if (texture) {
      modelElement.textureFromData(texture);
    }

    modelElement.color = colorHex;
  });
}

// eslint-disable-next-line require-yield
export function* updateWindowGeometryForElementsSaga({ payload }) {
  const { nodeIds, attributes, color } = payload;
  const dimensionality = 3;

  const secondOrderDetailAttributes =
    SECOND_ORDER_DETAIL_ATTRIBUTES_BY_PARAMETRIC_TYPE.window || {};
  // secondOrderDetailAttributes are only the common attributes that apply to all of
  // the windows; these do not include frameType and grilleType, which are unique to each
  // window, come from the payload, and do not get ovewritten by the merging here.
  Object.assign(attributes, secondOrderDetailAttributes);

  for (const uuid of nodeIds) {
    const node = macheteReduxUtils.getNode(uuid);
    const { segmentedModelElements = [] } = node;

    for (const element of segmentedModelElements) {
      element.color = color;
      element.configureParametricGeometry({
        enabled: true,
        type: PARAMETRIC_TYPES.WINDOW,
        dimensionality,
        attributes,
      });
    }
  }
}

export function* trackWebGLCapabilitiesSaga() {
  const { webGLCapabilities } = getViewer();

  yield put(
    trackingDux.actions.track(EventNames.viewer.WEBGL_CAPABILITIES, {
      web_gl_capabilities: webGLCapabilities,
      gpu_name: webGLCapabilities.GPU_NAME,
      vendor_name: webGLCapabilities.VENDOR_NAME,
    }),
  );
}

export function* trackRotate() {
  const render_mode = yield select(appDux.selectors.getActiveRenderMode);

  yield put(
    trackingDux.actions.track(EventNames.viewer.MODEL_INTERACTION, {
      interaction_type: 'rotate',
      render_mode,
    }),
  );
}

export function* trackZoom() {
  const render_mode = yield select(appDux.selectors.getActiveRenderMode);

  yield put(
    trackingDux.actions.track(EventNames.viewer.MODEL_INTERACTION, {
      interaction_type: 'zoom',
      render_mode,
    }),
  );
}

export const throttleTrackRotate = throttle(trackRotate, 3000);
export const throttleTrackZoom = throttle(trackZoom, 3000);

export function* macheteEventsSaga() {
  const zoomEventChannel = yield call(macheteEventChannels.zoom);
  const rotateEventChannel = yield call(macheteEventChannels.rotate);

  while (true) {
    const { rotate, zoom } = yield race({
      rotate: take(rotateEventChannel),
      zoom: take(zoomEventChannel),
    });

    if (rotate) {
      yield call(throttleTrackRotate);
    } else if (zoom) {
      yield call(throttleTrackZoom);
    }
  }
}

export function* initializeMacheteEventsSaga() {
  yield fork(macheteEventsSaga);
}

function changeDimensionality(parametricModelElements, dimensionality) {
  parametricModelElements.forEach((parametricModelElement) => {
    // parametricOptions is a completely transparent object. Its fields are meant
    // to be known by clients.
    const parametricOptions = {
      ...parametricModelElement.parametricOptions,
    };
    parametricOptions.dimensionality = dimensionality;
    parametricModelElement.configureParametricGeometry(parametricOptions);
  });
}

function getElementsWithAllowedSecondOrderDetail(
  allowedSecondOrderDetailParametricTypes = [],
) {
  const elementsOfAllowedTypes = getModel().elements.filter((element) =>
    allowedSecondOrderDetailParametricTypes.includes(
      element?.parametricOptions?.type,
    ),
  );
  return elementsOfAllowedTypes;
}

export function* enableSecondOrderDetailSaga() {
  yield call(setSecondOrderDetailSaga, true);
}

export function* disableSecondOrderDetailSaga() {
  yield call(setSecondOrderDetailSaga, false);
}

// eslint-disable-next-line require-yield
export function* setSecondOrderDetailSaga(enabled) {
  const elementsToChangeDimensionality = getElementsWithAllowedSecondOrderDetail(
    SECOND_ORDER_DETAIL_PARAMETRIC_TYPES,
  );

  if (elementsToChangeDimensionality.length === 0) {
    return;
  }

  const dimensionality = enabled ? 3 : 2;
  changeDimensionality(elementsToChangeDimensionality, dimensionality);
}

export function getSecondOrderDetailParametricOptions(
  allowedSecondOrderDetailParametricTypes = [],
) {
  const parametricOptionsByType = {};

  for (const parametricType of allowedSecondOrderDetailParametricTypes) {
    const attributes =
      constants.machete.SECOND_ORDER_DETAIL_ATTRIBUTES_BY_PARAMETRIC_TYPE[
        parametricType
      ] || {};

    parametricOptionsByType[parametricType] = {
      enabled: true,
      type: parametricType,
      dimensionality: 3,
      attributes,
    };
  }

  return parametricOptionsByType;
}

export function* toggleMeasureTool() {
  const measureTool = yield select(macheteDux.selectors.getMeasureTool);
  const { enabled } = measureTool;

  measureTool.setModel(getModel());

  if (enabled) {
    measureTool.disable();
  } else {
    measureTool.enable();
  }
}

export function* toggleMeasureToolSaga() {
  yield call(toggleMeasureTool);
}

export default function* sagas() {
  yield all([
    fork(handlePointerEventsSaga),
    takeLatest(
      macheteDux.actions.outlineSelectedElements,
      updateModelElementOutlines,
    ),
    takeLatest(macheteDux.actions.panCamera, panCameraSaga),
    takeLatest(macheteDux.actions.zoomIn, zoomInSaga),
    takeLatest(macheteDux.actions.zoomOut, zoomOutSaga),
    takeLatest(
      macheteDux.actions.updateElementAttributes,
      updateElementTextureSaga,
    ),
    takeLatest(
      macheteDux.actions.updateFeatureAttributes,
      updateFeatureTextureSaga,
    ),
    takeLatest(
      macheteDux.actions.initializeMacheteEvents,
      initializeMacheteEventsSaga,
    ),
    takeLatest(
      macheteDux.actions.updateElementWindowGeometry,
      updateWindowGeometryForElementsSaga,
    ),
    takeLatest(
      [
        macheteActions.segmentation.addNodeSelection,
        macheteActions.segmentation.clearNodeSelection,
        macheteActions.segmentation.setNodeSelection,
        macheteActions.segmentation.removeNodeSelection,
      ],
      handleNodeSelectionChangedSaga,
    ),
    takeLatest(macheteDux.actions.enable2od, enableSecondOrderDetailSaga),
    takeLatest(macheteDux.actions.disable2od, disableSecondOrderDetailSaga),
    takeLatest(macheteDux.actions.toggleMeasureTool, toggleMeasureToolSaga),
  ]);
}
